import get from 'lodash/get'
import defaultTo from 'lodash/defaultTo'
import isPlainObject from 'lodash/isPlainObject'

import apiClient from '@services/ApiClient'

/**
 * @class Collection
 */
export default class Collection {
  /**
   * Create collection instance.
   *
   * @param {Array} models
   */
  constructor (models = [], options = {}) {
    this.$settings = Object.assign({
      limit: 25
    }, options)

    this.$models = []

    this.$page = 0
    this.$total = 0
    this.$limit = this.$settings.limit
    this.$loading = false

    this.addAll(models)
  }

  /**
   * Check whether collection has any items loaded.
   *
   * @return {Boolean}
   */
  get $empty () {
    return !this.$models.length
  }

  /**
   * Get collection item count.
   *
   * @return {Number}
   */
  get $length () {
    return this.$models.length
  }

  /**
   * Add multiple model items into collection.
   *
   * @param {Array} models
   * @return {Collection}
   */
  addAll (models = []) {
    for (let i = 0; i < models.length; i++) {
      this.add(models[i])
    }
    return this
  }

  /**
   * Replace current collection items with new batch.
   *
   * @param {Array} models
   * @return {Collection}
   */
  replaceAll (models = []) {
    const result = []

    for (let i = 0; i < models.length; i++) {
      result.push(this.create(models[i]))
    }

    this.$models = result

    return this
  }

  /**
   * Add single model item into collection.
   *
   * @param {Model} model
   * @return {Collection}
   */
  add (model) {
    this.$models.push(this.create(model))
    return this
  }

  /**
   * Remove all collection items.
   *
   * @return {Collection}
   */
  clear () {
    this.$page = 0
    this.$total = 0
    this.$models = []

    return this
  }

  /**
   * Create collection item instance.
   *
   * @param  {Object} model
   * @return {Model}
   */
  create (model = {}) {
    if (model instanceof this.getModel()) {
      return model
    }

    return new (this.getModel())(model)
  }

  /**
   * Load collection items from back-end server.
   *
   * @param  {Array|String} resource
   * @return {Promise}
   */
  fetch (resource, query) {
    let queryData

    if (isPlainObject(query)) {
      queryData = this.prepareQueryData(query)
    }

    return this.request('GET', resource, queryData)
      .then(data => {
        return this.replaceAll(data.payload)
          .setPage(get(data, 'pageInfo.pageIndex', 0))
          .setLimit(get(data, 'pageInfo.pageSize', this.$settings.limit))
          .setTotal(get(data, 'pageInfo.totalCount', 0))
          .toArray()
      })
  }

  /**
   * Send request to back-end server.
   *
   * @param  {String} method
   * @param  {Array|String} resource
   * @param  {Object} [data]
   * @return {Promise}
   */
  request (method, resource, data = {}) {
    this.setLoading(true)

    return apiClient.sendRequest(method, resource, data)
      .finally(_ => {
        this.setLoading(false)
      })
      .then(data => {
        return data
      })
  }

  /**
   * Prepare query data that contains pagination and filtering information.
   *
   * @return {Object}
   */
  prepareQueryData (query) {
    return {
      page: defaultTo(query.page, 0),
      size: defaultTo(query.limit, this.$settings.limit)
      // @todo Filters
    }
  }

  /**
   * Set total collection items count, used in pagination.
   *
   * @param {Number} total
   * @return {Collection}
   */
  setTotal (total) {
    this.$total = total
    return this
  }

  /**
   * Set collection item limit per page.
   *
   * @param {Number} limit
   * @return {Collection}
   */
  setLimit (limit) {
    this.$limit = limit
    return this
  }

  /**
   * Set page number after.
   *
   * @param {Number} page
   * @return {Collection}
   */
  setPage (page) {
    this.$page = page
    return this
  }

  /**
   * Set loading state.
   *
   * @param {Boolean} loading
   * @return {Collection}
   */
  setLoading (loading) {
    this.$loading = loading
    return this
  }

  /**
   * Get loading state.
   *
   * @return {Boolean}
   */
  isLoading () {
    return this.$loading
  }

  /**
   * Default collection item constructor.
   *
   * @return {Function}
   */
  getModel () {
    return Object
  }

  /**
   * Returns collection items array.
   *
   * @return {Array}
   */
  toArray () {
    return this.$models
  }
}



// WEBPACK FOOTER //
// ./src/models/Collection.js